import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { AppRouter } from '@/app/router';
import { ToastViewport } from '@/components/ui';
import { PullToRefresh } from '@/components/PullToRefresh';
import { I18nProvider } from '@/lib/i18n';
import { MaintenanceCheck } from '@/features/maintenance/MaintenanceCheck';
import { registerSW } from 'virtual:pwa-register';
import '@/app/styles.css';

const UPDATE_CHECK_INTERVAL_MS = 30_000;
const UPDATE_RELOAD_FALLBACK_MS = 5_000;
const UPDATE_QUERY_KEY = '_app_build';

type AppVersion = {
  buildId?: string;
};

let updateSW: ((reloadPage?: boolean) => Promise<void>) | null = null;
let checkingForUpdate = false;
let reloadingForUpdate = false;
let controlledByServiceWorker = Boolean(navigator.serviceWorker?.controller);
let pendingBuildId: string | null = null;
let fallbackReloadTimer: number | null = null;

function cleanUpdateQueryFromAddressBar() {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(UPDATE_QUERY_KEY)) return;
  url.searchParams.delete(UPDATE_QUERY_KEY);
  window.history.replaceState(window.history.state, '', url.toString());
}

function reloadIntoBuild(buildId: string) {
  if (reloadingForUpdate) return;
  reloadingForUpdate = true;
  const url = new URL(window.location.href);
  url.searchParams.set(UPDATE_QUERY_KEY, buildId);
  window.location.replace(url.toString());
}

async function checkForAppUpdate(registration?: ServiceWorkerRegistration) {
  if (checkingForUpdate || reloadingForUpdate) return;
  checkingForUpdate = true;

  try {
    const response = await fetch(`/app-version.json?t=${Date.now()}`, {
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache' },
    });
    if (!response.ok) return;

    const remoteVersion = await response.json() as AppVersion;
    const remoteBuildId = String(remoteVersion.buildId || '').trim();
    if (!remoteBuildId || remoteBuildId === __BUILD_TIMESTAMP__) return;

    pendingBuildId = remoteBuildId;
    await registration?.update().catch(() => {});
    await updateSW?.(true).catch(() => {});

    if (fallbackReloadTimer !== null) window.clearTimeout(fallbackReloadTimer);
    fallbackReloadTimer = window.setTimeout(() => {
      // If an older browser does not emit controllerchange, remove only the
      // stale worker registration and reload from the network. Browser user
      // data (login, localStorage and IndexedDB) is intentionally untouched.
      void navigator.serviceWorker.getRegistration()
        .then((activeRegistration) => activeRegistration?.unregister())
        .catch(() => false)
        .finally(() => reloadIntoBuild(remoteBuildId));
    }, UPDATE_RELOAD_FALLBACK_MS);
  } catch {
    // Offline or temporarily unavailable: keep the current app and retry later.
  } finally {
    checkingForUpdate = false;
  }
}

cleanUpdateQueryFromAddressBar();

if ('serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloadingForUpdate) return;

    // Do not reload a first-time visitor just because the initial worker was
    // installed. Subsequent controller changes represent an app update.
    if (!controlledByServiceWorker && !pendingBuildId) {
      controlledByServiceWorker = true;
      return;
    }

    controlledByServiceWorker = true;
    if (fallbackReloadTimer !== null) window.clearTimeout(fallbackReloadTimer);
    reloadIntoBuild(pendingBuildId || Date.now().toString());
  });
}

updateSW = registerSW({
  immediate: true,
  onRegisteredSW(_swUrl, registration) {
    if (!registration) return;

    void checkForAppUpdate(registration);
    window.setInterval(() => {
      void checkForAppUpdate(registration);
    }, UPDATE_CHECK_INTERVAL_MS);

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        void checkForAppUpdate(registration);
      }
    });
    window.addEventListener('online', () => {
      void checkForAppUpdate(registration);
    });
  },
});

const queryClient = new QueryClient();

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <I18nProvider>
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <PullToRefresh />
          <MaintenanceCheck>
            <AppRouter />
            <ToastViewport />
          </MaintenanceCheck>
        </BrowserRouter>
      </I18nProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);
